import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  loadAdminData,
  loadRosterData,
  type AdminCompanySettingsRow,
  type AdminData,
  type AdminLocationRow,
  type EmployeeRow,
  type HealthCheckFinding,
  type HealthCheckSeverity,
  type ManagerRow,
  type RosterData,
} from './data';
import {
  createEmployee,
  createLocation,
  provisionManager,
  removeManager,
  updateCompanySettings,
  updateEmployee,
  updateLocation,
  updateManager,
  type ActionResult,
} from '@/lib/actions';
import { TechnicalDetails } from '@/lib/TechnicalDetails';
import { TableScroller } from '@/lib/TableScroller';
import { EmailChipsInput, type EmailChipsInputHandle } from '@/lib/EmailChipsInput';
import { useIsNarrow } from '@/lib/useMediaQuery';
import { color, font, radius, shadow } from '@/lib/theme';

/**
 * The admin console (P4) — Roster, Managers & Access, Locations, Company
 * settings, per ADMIN_PORTAL_HANDOFF §5's four areas (D4). Its own sidebar
 * destination, gated by `manager.role === 'admin'`
 * both here (so a non-admin gets a clear message instead of empty tables)
 * and, for real, by RLS (migration 0016's `adm_all` / `adm_write` policies —
 * that is the actual security boundary; this component's own check is UX).
 *
 * Every write goes through lib/actions.ts and is audited server-side by
 * trigger (audit_log_writer, M9) — nothing here composes an audit entry;
 * that would be exactly the "a write path can forget" failure mode M9
 * exists to end.
 */

type Tab = 'roster' | 'managers' | 'locations' | 'settings' | 'health';

/**
 * What a location-scoped manager may do on the Roster, per migration 0020
 * (pre-P8 punch list Tier 4 — a deliberate reversal of AP-D4).
 *
 * `null` everywhere below means "the admin console's own unrestricted view".
 * A non-null scope narrows the SAME components — one Roster UI, two callers,
 * rather than a second copy that can drift — to what the database will
 * actually accept from that manager:
 *
 *   * add / edit / deactivate at `locationId`, and nothing outside it;
 *   * NO reassignment (Scott's explicit 2026-09-04 boundary);
 *   * NO individual PTO allotment.
 *
 * Both restrictions are enforced by `employees_manager_scope` in the
 * database, which is the real boundary — this type only stops the UI from
 * offering a control whose only possible outcome is a refusal.
 */
interface ManagerRosterScope {
  locationId: string;
}

const TABS: Array<{ key: Tab; label: string }> = [
  { key: 'roster', label: 'Roster' },
  { key: 'managers', label: 'Managers & Access' },
  { key: 'locations', label: 'Locations' },
  { key: 'settings', label: 'Company Settings' },
  { key: 'health', label: 'Health Checks' },
];

export function AdminConsole({
  manager,
  onChanged,
}: {
  manager: ManagerRow | null;
  /**
   * Adversarial-review fix (P6): Dashboard never remounts AdminConsole on a
   * hash change (it's a sibling view, not a route), and until this prop
   * existed it had no way to hear about a write made here at all — so an
   * admin who reassigned an employee's location, then switched to the
   * Dashboard or Manage Records in the same session, saw those pages' own
   * location filters (and anything else keyed off their employees/locations
   * snapshot) still running on the pre-edit data until a full page reload.
   * Optional so a future standalone use of this component isn't forced to
   * supply a no-op.
   */
  onChanged?: () => void;
}) {
  const [tab, setTab] = useState<Tab>('roster');
  const [data, setData] = useState<AdminData | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Direction D pattern 1/3 (page title + card container) both carry a narrow
  // override, matching the already-shipped Dashboard/LogAbsence convention.
  const narrow = useIsNarrow();

  const isAdmin = manager?.role === 'admin';

  // Gated on isAdmin INSIDE the effect body, not by skipping the useEffect
  // call itself — React's Rules of Hooks forbid a conditional hook, and this
  // component's early return below happens after every hook call anyway.
  // Without this guard a non-admin who reaches this component (client-side
  // routing only, never the real boundary) would still fire loadAdminData()
  // on mount for no reason — RLS still returns nothing they shouldn't see,
  // but there is no reason to make the round trip at all.
  const load = useCallback(() => {
    if (!isAdmin) return;
    setError(null);
    loadAdminData().then(setData, (e: unknown) => setError(String(e)));
  }, [isAdmin]);

  useEffect(load, [load]);

  // Refreshes THIS console's own view and tells the parent (Dashboard) to
  // refresh its separate, already-loaded employees/locations snapshot too —
  // every tab below calls this instead of `load` directly after a write.
  const refreshAfterWrite = useCallback(() => {
    load();
    onChanged?.();
  }, [load, onChanged]);

  if (!isAdmin) {
    return (
      <div style={S.wrap}>
        {/* Accessibility-review fix (redesign Pass 2): same "both refusal
            states get the same heading as the real page" discipline
            ManagerRoster's own !canManage branch already follows below —
            this state had no heading at all before, reachable by typing
            the #/admin hash directly (the sidebar already hides the nav
            item, but the hash route itself isn't guarded). */}
        <h1 style={{ ...S.title, ...(narrow ? S.titleNarrow : null) }}>Admin Console</h1>
        <p style={S.sub}>
          The admin console is visible only to admins. If you believe this is
          wrong, ask an existing admin to check your role in Managers &amp;
          Access.
        </p>
      </div>
    );
  }

  return (
    <div style={S.wrap}>
      {/* Accessibility-review fix (redesign Pass 2): this page's own title
          is now the document's h1 (AppShell supplies no heading of its
          own) — matching the Dashboard view's already-shipped pattern.
          Every nested tab's own heading below shifted from h3 to h2 to
          keep a clean, unskipped hierarchy under it. */}
      <h1 style={{ ...S.title, ...(narrow ? S.titleNarrow : null) }}>Admin Console</h1>
      <p style={S.sub}>
        Employees, managers, locations, and company-wide settings. Every
        change here is logged (who, what, old value, new value) and takes
        effect immediately — no re-login, no deploy.
      </p>

      <nav aria-label="Admin sections" style={S.tabs}>
        {TABS.map((t) => {
          // A critical finding gets its own loud badge on the tab itself —
          // the whole point of a health check is that nobody is already
          // looking at this page, so the summons has to reach Roster/
          // Managers/wherever an admin actually lands first.
          const criticalCount =
            t.key === 'health'
              ? (data?.healthChecks.filter((f) => f.severity === 'critical').length ?? 0)
              : 0;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              style={{ ...S.tabBtn, ...(tab === t.key ? S.tabBtnActive : null) }}
            >
              {t.label}
              {t.key === 'health' && data && data.healthChecks.length > 0 && (
                <span style={criticalCount > 0 ? S.tabBadgeCritical : S.tabBadge}>
                  {data.healthChecks.length}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {error && <p style={S.errorBanner}>Could not load admin data: {error}</p>}
      {!data && !error && <p style={S.sub}>Loading…</p>}

      {data && (
        <div style={{ ...S.card, ...(narrow ? S.cardNarrow : null) }}>
          {tab === 'roster' && (
            <RosterTab
              employees={data.employees}
              locations={data.locations}
              onChanged={refreshAfterWrite}
            />
          )}
          {tab === 'managers' && (
            <ManagersTab
              managers={data.managers}
              locations={data.locations}
              onChanged={refreshAfterWrite}
            />
          )}
          {tab === 'locations' && (
            <LocationsTab locations={data.locations} onChanged={refreshAfterWrite} />
          )}
          {tab === 'settings' && (
            <SettingsTab settings={data.settings} onChanged={refreshAfterWrite} />
          )}
          {tab === 'health' && (
            <HealthChecksTab findings={data.healthChecks} onRefresh={refreshAfterWrite} />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The manager-facing Roster page (migration 0020, pre-P8 punch list Tier 4).
 *
 * Same RosterTab the admin console renders, narrowed by `managerScope` — one
 * Roster UI with two callers rather than a second copy that can drift out of
 * step with the first. What differs is only the shell: a page of its own,
 * its own sidebar destination, its own two-table read (loadRosterData), and
 * no tabs, because the other four areas are genuinely admin-only.
 *
 * 🔴 The gate is `role !== 'admin' && location_id !== null`, which is exactly
 * `manages_location()`'s own predicate rather than an approximation of it:
 *
 *   * an ADMIN is sent away from this page — not because they lack authority
 *     but because they already reach the identical UI through the Admin
 *     Console's Roster tab, and two front doors to one screen was the "Manager
 *     Home is confusing" finding this punch list already fixed once;
 *   * a NULL-LOCATION manager is sent away because the database will refuse
 *     every write they attempt (manages_location() requires a real location
 *     match — see the migration's section 1 for why the write predicate does
 *     not inherit the read side's "NULL sees everything" rule). Showing them
 *     Add and Edit buttons that can only ever fail would be a worse bug than
 *     not showing the page.
 *
 * As everywhere else in this app, the component's own check is UX: RLS is the
 * boundary, and a manager who reached this view by typing the hash would see
 * their own location's roster and nothing else regardless.
 */
export function ManagerRoster({
  manager,
  onChanged,
}: {
  manager: ManagerRow | null;
  /** Refreshes the Dashboard's separately-loaded employee snapshot, same as AdminConsole's. */
  onChanged?: () => void;
}) {
  const [data, setData] = useState<RosterData | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Direction D pattern 1/3 narrow overrides, same as AdminConsole above.
  const narrow = useIsNarrow();

  const locationId = manager?.role !== 'admin' ? (manager?.location_id ?? null) : null;
  const canManage = locationId !== null;

  // Gated inside the effect body, not by skipping the hook — React's Rules of
  // Hooks forbid a conditional hook, and the early return below runs after
  // every hook call anyway (the same shape AdminConsole's loader uses).
  const load = useCallback(() => {
    if (!canManage) return;
    setError(null);
    loadRosterData().then(setData, (e: unknown) => setError(String(e)));
  }, [canManage]);

  useEffect(load, [load]);

  const refreshAfterWrite = useCallback(() => {
    load();
    onChanged?.();
  }, [load, onChanged]);

  // Both refusal states get the same heading as the real page and a next step
  // the reader can actually take (adversarial-review finding: the first draft
  // sent an admin to a tab with no link, and told a NULL-location manager to
  // "check" a tab that is admin-only — a dead end from a page they reached by
  // typing the hash, which is the only way to land here at all).
  if (!canManage) {
    return (
      <div style={S.wrap}>
        {/* Accessibility-review fix (redesign Pass 2): this page's own
            title is now the document's h1, matching the Dashboard view's
            already-shipped pattern (AppShell supplies no heading of its
            own). */}
        <h1 style={{ ...S.title, ...(narrow ? S.titleNarrow : null) }}>Roster</h1>
        {manager?.role === 'admin' ? (
          <p style={S.sub}>
            Admins manage the roster in the{' '}
            <a href="#/admin" style={S.inlineLink}>
              Admin Console
            </a>{' '}
            — Roster is its first tab, with every location in one list.
          </p>
        ) : (
          <p style={S.sub}>
            Your manager account covers every location, so you can see all of
            them on the dashboard — but this page only ever edits one location&rsquo;s
            roster, so there is nothing here for it to edit. Ask an admin to
            assign you to a location if you should be managing one.
          </p>
        )}
      </div>
    );
  }

  // The manager's `locations` read is RLS-scoped to their own site, so the one
  // row it returns names this page. Worth showing: a manager who also reads
  // other locations on the Dashboard otherwise has no on-screen confirmation
  // of which roster they are editing (adversarial-review finding).
  const scopeName = data?.locations.find((l) => l.id === locationId)?.name ?? null;

  return (
    <div style={S.wrap}>
      {/* Accessibility-review fix (redesign Pass 2): same reasoning as the
          !canManage branch above — this page's own title is the document's
          h1 now. */}
      <h1 style={{ ...S.title, ...(narrow ? S.titleNarrow : null) }}>
        Roster{scopeName ? ` — ${scopeName}` : ''}
      </h1>
      <p style={S.sub}>
        Add someone new, correct a name or email, or mark someone inactive when
        they leave — for your own location. Records are never deleted, and every
        change here is logged. Moving someone to a different location, or giving
        one person their own PTO allotment, stays with an admin.
      </p>

      {error && <p style={S.errorBanner}>Could not load the roster: {error}</p>}
      {!data && !error && <p style={S.sub}>Loading…</p>}

      {data && (
        <div style={{ ...S.card, ...(narrow ? S.cardNarrow : null) }}>
          <RosterTab
            employees={data.employees}
            locations={data.locations}
            onChanged={refreshAfterWrite}
            managerScope={{ locationId }}
          />
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared bits
// ---------------------------------------------------------------------------

type Banner = { kind: 'success' | 'error'; text: string; detail?: string; seq: number } | null;

// Every banner gets a fresh sequence number so ResultBanner remounts its
// <p role=alert|status> on EVERY result — a second identical failure (the
// retry-on-bad-wifi case this whole item is about) used to update the same
// node with the same text, which is no DOM mutation at all: nothing announced,
// nothing visibly changed (review finding, a11y lens).
let bannerSeq = 0;

// Announced: errors as an alert, successes as status (pre-P8 punch list,
// Tier 2/3) — and a failure's raw wire text behind "Technical details".
function ResultBanner({ banner }: { banner: Banner }) {
  const ref = useRef<HTMLDivElement>(null);
  // The banner sits at the top of the tab; on a phone the edit form that just
  // closed was often far below it, so an error (a stale conflict especially)
  // would land off-screen — the admin would see their values change with no
  // explanation in view (review finding). Bring it into view.
  // Focusing (tabIndex -1) both scrolls it into view and gives keyboard and
  // screen-reader users an anchor — the edit form that had focus has just
  // unmounted (same mechanism as ErrorBoundary's fallback).
  useEffect(() => {
    if (banner?.kind === 'error') ref.current?.focus();
  }, [banner?.seq, banner?.kind]);
  if (!banner) return null;
  return (
    <div
      key={banner.seq}
      ref={ref}
      tabIndex={banner.kind === 'error' ? -1 : undefined}
      style={banner.kind === 'success' ? S.successBanner : S.errorBanner}
    >
      <p role={banner.kind === 'success' ? 'status' : 'alert'} style={S.bannerText}>
        {banner.text}
      </p>
      <TechnicalDetails detail={banner.detail} />
    </div>
  );
}

/** A neutral/positive outcome that is not an ActionResult (e.g. "already removed"). */
function noticeBanner(text: string): Banner {
  return { kind: 'success', text, seq: ++bannerSeq };
}

function bannerFrom(res: ActionResult, successText: string): Banner {
  const seq = ++bannerSeq;
  return res.ok
    ? { kind: 'success', text: successText, seq }
    : { kind: 'error', text: res.error ?? 'Something went wrong. Please try again.', detail: res.detail, seq };
}

/** "10,15,20" -> [10,15,20]. Blank -> null (inherit). Throws on a bad number. */
function parseNumberList(raw: string): number[] | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  return trimmed.split(',').map((s) => {
    const n = Number(s.trim());
    if (Number.isNaN(n)) throw new Error(`"${s.trim()}" is not a number`);
    return n;
  });
}

/**
 * The PTO-allotment fields (Roster, Location override, Company Settings).
 * Blank is returned as null and means whatever the caller's label says
 * (`blankMeaning`: "inherit" on the two override forms, "no limit" on
 * Company Settings — the error must not contradict the label above it).
 * Anything else must be a plain number of days: digits, at most two decimals
 * (the column is numeric(5,2), which would otherwise round silently), under
 * 1000 (which it would otherwise refuse with a bare SQLSTATE). Pre-P8 punch
 * list, Tier 3: `Number('abc')` is NaN, which JSON serialises as null, so a
 * typo used to save SILENTLY as blank with no error anywhere. Throws a plain
 * sentence for the form's own error line.
 */
function parseAllotment(raw: string, fieldLabel: string, blankMeaning: string): number | null {
  const trimmed = raw.trim();
  if (trimmed === '') return null;
  const n = Number(trimmed);
  if (!/^\d+(\.\d{1,2})?$/.test(trimmed) || !Number.isFinite(n) || n >= 1000) {
    throw new Error(
      `${fieldLabel} must be a number of days (up to two decimals, under 1000), or left blank for ${blankMeaning}.`,
    );
  }
  return n;
}

/**
 * Location option text for every dropdown that picks one — the Roster form
 * has always appended "(inactive)"; the Managers & Access forms did not
 * (pre-P8 punch list, Tier 3), so an admin could scope a new manager to a
 * closed site with nothing on screen saying so.
 */
function locationOptionLabel(l: AdminLocationRow): string {
  return l.active ? l.name : `${l.name} (inactive)`;
}

function locationName(locations: AdminLocationRow[], id: string | null): string {
  if (!id) return 'All locations';
  return locations.find((l) => l.id === id)?.name ?? '(unknown location)';
}

// ---------------------------------------------------------------------------
// Roster
// ---------------------------------------------------------------------------

/**
 * Focus handling for a tab's edit form. The form renders above the table,
 * far from the row's Edit button that opened it, and unmounts on Cancel or a
 * successful save — which used to leave focus on <body> (review finding).
 * Each Edit button registers itself by row key; closing hands focus back to
 * that button, or to the tab heading if the row is hidden or gone. A
 * failed/stale save is NOT handed back: the result banner takes focus itself
 * so the explanation is what gets read.
 */
function useEditFocus<K>() {
  const buttons = useRef(new Map<K, HTMLButtonElement>());
  const heading = useRef<HTMLHeadingElement | null>(null);
  const register = (key: K) => (el: HTMLButtonElement | null) => {
    if (el) buttons.current.set(key, el);
    else buttons.current.delete(key);
  };
  const returnTo = (key: K) => {
    // After React has re-rendered the row's Edit button back into place.
    requestAnimationFrame(() => (buttons.current.get(key) ?? heading.current)?.focus());
  };
  return { register, returnTo, heading };
}

function RosterTab({
  employees,
  locations,
  onChanged,
  managerScope = null,
}: {
  employees: EmployeeRow[];
  locations: AdminLocationRow[];
  onChanged: () => void;
  /** Non-null when this is a location-scoped manager's own Roster page (0020). */
  managerScope?: ManagerRosterScope | null;
}) {
  const [banner, setBanner] = useState<Banner>(null);
  // The row as it was when Edit was tapped — frozen, never re-derived from
  // the list (see the note above the form).
  const [editingRow, setEditingRow] = useState<EmployeeRow | null>(null);
  const editingId = editingRow?.id ?? null;
  const focus = useEditFocus<string>();
  const [adding, setAdding] = useState(false);
  // Pre-P8 punch list: former employees (deactivated, never deleted — DD2)
  // used to pile up in this list forever with no way to get them out of the
  // way. Default to active-only; the count on the toggle itself is the
  // affordance that says something is hidden, so this never reads as a
  // silent gap.
  const [showInactive, setShowInactive] = useState(false);
  const inactiveCount = employees.filter((e) => !e.active).length;
  const visibleEmployees = showInactive ? employees : employees.filter((e) => e.active);
  // Direction D pattern 4 (section heading) narrow override.
  const narrow = useIsNarrow();

  function closeEdit(handBackFocus: boolean) {
    const id = editingId;
    setEditingRow(null);
    if (handBackFocus && id !== null) focus.returnTo(id);
  }

  return (
    <div>
      <div style={S.tabHeader}>
        {/* Accessibility-review fix (redesign Pass 2): h3 -> h2, since the
            Admin Console's own top title above is now an h1, not an h2 —
            keeps this section heading one level below its page title
            instead of skipping a level. */}
        <h2
          ref={focus.heading}
          tabIndex={-1}
          style={{ ...S.tabTitle, ...(narrow ? S.tabTitleNarrow : null) }}
        >
          Roster ({visibleEmployees.length})
        </h2>
        {/* Add and Edit can both be open (Add renders first); closing one
            never discards what was typed in the other. */}
        <button style={S.addBtn} onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : '+ Add employee'}
        </button>
      </div>
      {inactiveCount > 0 && (
        <label style={S.showInactiveLabel}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive ({inactiveCount})
        </label>
      )}
      <ResultBanner banner={banner} />

      {adding && (
        <EmployeeForm
          locations={locations}
          managerScope={managerScope}
          onCancel={() => setAdding(false)}
          onSubmit={async (input) => {
            const res = await createEmployee(input);
            setBanner(bannerFrom(res, `${input.full_name} added to the Roster.`));
            if (res.ok) {
              setAdding(false);
              onChanged();
            }
            return res;
          }}
        />
      )}

      {/* Pre-P8 punch list, Tier 2 (mobile): the edit form used to REPLACE its
          table row. Inside a table that now scrolls sideways on a phone, a
          480px form would sit half off-screen — and the admin who tapped Edit
          in the rightmost column is scrolled away from it. It renders up here
          instead (where "+ Add" already does), on every width for one
          consistent layout; the row keeps its place, is tinted, and says
          "Editing…", and the form itself takes focus (its "Editing X" caption
          is what a screen reader hears first — no keyboard pops up for a
          field the admin rarely changes). Closing it hands focus back to the
          row's Edit button. The form gets the row AS IT WAS WHEN EDIT WAS
          TAPPED, not a live lookup: that frozen row is the compare-and-set
          snapshot (lib/actions.ts, matchSnapshot), and a refresh mid-edit
          must not swap it under fields still seeded from the old values. */}
      {editingRow && (
        <EmployeeForm
          key={editingRow.id}
          locations={locations}
          managerScope={managerScope}
          initial={editingRow}
          onCancel={() => closeEdit(true)}
          onSubmit={async (input) => {
            const res = await updateEmployee(editingRow.id, input, editingRow);
            setBanner(bannerFrom(res, `${input.full_name} updated.`));
            // A stale result closes the form and refreshes, same as success,
            // so the latest values are what the admin sees next — but the
            // banner keeps focus there, since it has the explanation.
            if (res.ok) {
              closeEdit(true);
              onChanged();
            } else if (res.kind === 'stale') {
              closeEdit(false);
              onChanged();
            }
            return res;
          }}
        />
      )}

      <TableScroller labelledBy="roster-caption" minWidth="44rem">
        <table style={S.table}>
          <caption id="roster-caption" style={S.srOnly}>
            Roster
          </caption>
          <thead>
            <tr>
              <th style={S.th}>Name</th>
              <th style={S.th}>Email</th>
              <th style={S.th}>Location</th>
              <th style={S.th}>PTO allotment</th>
              <th style={S.th}>Status</th>
              <th style={S.thActions}>
                <span style={S.srOnly}>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleEmployees.map((e) => {
              // Dim the DATA cells of an inactive row, not the row: the
              // actions cell keeps full contrast (review finding — "Editing…"
              // at 55% opacity fell to ~2.8:1).
              const td = e.active ? S.td : S.tdInactive;
              return (
                <tr key={e.id} style={editingId === e.id ? S.rowEditing : undefined}>
                  <td style={td}>{e.full_name}</td>
                  <td style={td}>{e.email ?? <span style={S.tdSub}>—</span>}</td>
                  <td style={td}>{locationName(locations, e.location_id)}</td>
                  <td style={td}>{e.pto_allotment ?? <span style={S.tdSub}>inherit</span>}</td>
                  <td style={td}>{e.active ? 'Active' : 'Inactive'}</td>
                  <td style={S.td}>
                    {editingId === e.id ? (
                      <span style={S.editingTag}>Editing…</span>
                    ) : (
                      <button ref={focus.register(e.id)} style={S.linkBtn} onClick={() => setEditingRow(e)}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroller>
    </div>
  );
}

function EmployeeForm({
  locations,
  initial,
  onCancel,
  onSubmit,
  managerScope = null,
}: {
  locations: AdminLocationRow[];
  initial?: EmployeeRow;
  onCancel: () => void;
  onSubmit: (input: {
    full_name: string;
    email: string | null;
    location_id: string;
    pto_allotment: number | null;
    active: boolean;
  }) => Promise<ActionResult>;
  /**
   * Non-null for a location-scoped manager (0020): Location and PTO allotment
   * become read-only captions instead of controls. Both are still SENT — the
   * write is a whole-row update whose compare-and-set snapshot includes them
   * (lib/actions.ts, EMPLOYEE_SNAPSHOT_COLUMNS) — but echoed back exactly as
   * loaded, so the database's own column guard sees no change to refuse.
   */
  managerScope?: ManagerRosterScope | null;
}) {
  const [fullName, setFullName] = useState(initial?.full_name ?? '');
  const [email, setEmail] = useState(initial?.email ?? '');
  const [locationId, setLocationId] = useState(
    initial?.location_id ?? managerScope?.locationId ?? locations[0]?.id ?? '',
  );
  const [allotment, setAllotment] = useState(initial?.pto_allotment?.toString() ?? '');
  const [active, setActive] = useState(initial?.active ?? true);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const captionId = useId();
  const formRef = useRef<HTMLDivElement | null>(null);
  // Editing: focus the form itself, so the "Editing X" caption is announced
  // and no keyboard pops up over the fields (review finding). Adding: the
  // Name field is the certain next step, so it keeps autoFocus.
  useEffect(() => {
    if (initial) formRef.current?.focus();
    // mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function submit() {
    if (!fullName.trim() || !locationId) return;
    setLocalError(null);
    let parsedAllotment: number | null;
    if (managerScope) {
      // Echo the loaded value, never a parsed field — a manager has no
      // allotment input to parse, and sending anything else is a refusal
      // (0020's column guard). NULL on Add is "inherit this location's
      // allotment", which is the only value a manager may create.
      parsedAllotment = initial?.pto_allotment ?? null;
    } else {
      try {
        parsedAllotment = parseAllotment(allotment, 'PTO allotment', 'inherit');
      } catch (e) {
        setLocalError((e as Error).message);
        return;
      }
    }
    setBusy(true);
    await onSubmit({
      full_name: fullName.trim(),
      email: email.trim() ? email.trim().toLowerCase() : null,
      location_id: locationId,
      pto_allotment: parsedAllotment,
      active,
    });
    setBusy(false);
  }

  return (
    <div
      ref={formRef}
      style={S.form}
      role={initial ? 'group' : undefined}
      aria-labelledby={initial ? captionId : undefined}
      tabIndex={initial ? -1 : undefined}
    >
      {/* Pre-P8 punch list: an identity caption so it's never ambiguous which
          row this card belongs to, independent of the table header above it.
          Uses `initial`'s ORIGINAL name — stable while the field below is
          mid-edit, rather than echoing back whatever's currently typed. */}
      {initial && (
        <p id={captionId} style={S.editingCaption}>
          Editing {initial.full_name}
        </p>
      )}
      {localError && (
        <p role="alert" style={S.errorBanner}>
          {localError}
        </p>
      )}
      <Field label="Name">
        <input
          autoFocus={!initial}
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          style={S.input}
        />
      </Field>
      {/* Trap #2 in reverse (adversarial-review finding): the roster's email
          is what approval and denial emails are addressed to, and a new hire
          added without one is reported ONLY in the admin-only Health Checks
          tab and the weekly digest — so the person who left it blank is the
          one person who never hears about it. Say what the field is for at
          the point it is filled in. */}
      <Field
        label="Email"
        hint="Where approval and denial emails go. Leave blank only if they have no address."
      >
        <input value={email} onChange={(e) => setEmail(e.target.value)} style={S.input} />
      </Field>
      {/* 0020: a manager gets captions, not controls, for the two fields the
          database will refuse from them — a disabled <select> holding one
          option, or an input that can only ever produce an error, is worse
          than a plain sentence saying who can change it. Each says the
          CURRENT value first, so nothing about the record is hidden. */}
      {managerScope ? (
        <>
          <Field
            label="Location"
            plain
            hint="Only an admin can move someone to another location — ask an admin."
          >
            <span>{locationName(locations, locationId)}</span>
          </Field>
          <Field
            label="PTO allotment"
            plain
            hint="Only an admin can set an individual PTO allotment — ask an admin."
          >
            <span>
              {initial?.pto_allotment == null
                ? 'Uses this location’s PTO allotment'
                : `${initial.pto_allotment} days`}
            </span>
          </Field>
        </>
      ) : (
        <>
          <Field label="Location">
            <select
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
              style={S.input}
            >
              {locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {locationOptionLabel(l)}
                </option>
              ))}
            </select>
          </Field>
          <Field label="PTO allotment (blank = inherit)">
            <input
              inputMode="decimal"
              value={allotment}
              onChange={(e) => setAllotment(e.target.value)}
              style={S.input}
            />
          </Field>
        </>
      )}
      {/* Deactivation used to be an admin action; since 0020 it is an everyday
          manager one, so its second-order effects need saying out loud
          (adversarial-review finding). An inactive employee drops off the
          request form, the Dashboard and Log an Absence — but a request they
          already have PENDING stays in the queue marked "(former employee)"
          with live Approve/Deny buttons, and approved time off still to come
          stays on the calendar. Both are fixable in Manage Records; until now
          the only thing that noticed was the admin-only weekly digest. */}
      {initial && (
        <Field
          label="Status"
          hint="Inactive people disappear from the request form, the dashboard and Log an Absence; their history is kept. If they have a request still pending, or approved time off still to come, decide or void it in Manage Records."
        >
          <label style={S.checkboxLabel}>
            <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
            Active
          </label>
        </Field>
      )}
      <div style={S.formActions}>
        <button onClick={() => void submit()} disabled={busy} style={S.saveBtn}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel} style={S.cancelBtn}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Managers & Access
// ---------------------------------------------------------------------------

function ManagersTab({
  managers,
  locations,
  onChanged,
}: {
  managers: ManagerRow[];
  locations: AdminLocationRow[];
  onChanged: () => void;
}) {
  const [banner, setBanner] = useState<Banner>(null);
  // The row as it was when Edit was tapped — frozen (see the note above the
  // form). Email is the key: it cannot change through the edit path.
  const [editingRow, setEditingRow] = useState<ManagerRow | null>(null);
  const editingEmail = editingRow?.email ?? null;
  const focus = useEditFocus<string>();
  const [adding, setAdding] = useState(false);
  // Pre-P8 punch list, Tier 2: no busy-guard existed against a double-submit
  // here — this is a real hard-DELETE (unlike every other admin table), so a
  // duplicate click is worth guarding against even though `window.confirm`
  // already narrows the window.
  const [removingEmail, setRemovingEmail] = useState<string | null>(null);
  // Direction D pattern 4 (section heading) narrow override.
  const narrow = useIsNarrow();

  function closeEdit(handBackFocus: boolean) {
    const email = editingEmail;
    setEditingRow(null);
    if (handBackFocus && email !== null) focus.returnTo(email);
  }

  // The manager being edited can vanish under us on a refresh (another admin
  // removed them, or this admin removed someone else and the reload dropped
  // them too). Close the form and say so rather than disappearing silently.
  useEffect(() => {
    if (editingRow && !managers.some((m) => m.email === editingRow.email)) {
      setEditingRow(null);
      setBanner(
        noticeBanner(`${editingRow.full_name} is no longer in the list, so that edit was closed.`),
      );
      focus.heading.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [managers, editingRow]);

  async function remove(m: ManagerRow) {
    if (!window.confirm(`Remove ${m.full_name} (${m.email}) as a manager? This cannot be undone.`)) {
      return;
    }
    setRemovingEmail(m.email);
    const res = await removeManager(m.email, m.full_name);
    setRemovingEmail(null);
    if (res.kind === 'stale') {
      // Already gone (another admin, another tab). The outcome the admin
      // wanted holds, so this is a notice, not a red "not saved" error —
      // the list is stale either way, so refresh.
      setBanner(
        noticeBanner(
          `${m.full_name} was already gone (removed by another admin or by you in another tab — or no longer visible to you). The list is refreshing.`,
        ),
      );
      onChanged();
      return;
    }
    setBanner(bannerFrom(res, `${m.full_name} removed.`));
    if (res.ok) onChanged();
  }

  return (
    <div>
      <div style={S.tabHeader}>
        {/* Accessibility-review fix (redesign Pass 2): h3 -> h2, matching
            RosterTab's own comment above. */}
        <h2
          ref={focus.heading}
          tabIndex={-1}
          style={{ ...S.tabTitle, ...(narrow ? S.tabTitleNarrow : null) }}
        >
          Managers &amp; Access ({managers.length})
        </h2>
        <button style={S.addBtn} onClick={() => setAdding((v) => !v)}>
          {adding ? 'Cancel' : '+ Add manager'}
        </button>
      </div>
      <ResultBanner banner={banner} />
      <p style={S.tdSub}>
        No PIN or password here — a new manager signs in with an emailed
        one-time code (M3). Adding someone below creates their sign-in
        account (pre-confirmed) as well as their access.
      </p>

      {adding && (
        <ManagerForm
          locations={locations}
          onCancel={() => setAdding(false)}
          onSubmit={async (input) => {
            const res = await provisionManager(input);
            setBanner(bannerFrom(res, `${input.fullName} added. They can sign in with ${input.email}.`));
            if (res.ok) {
              setAdding(false);
              onChanged();
            }
            return res;
          }}
        />
      )}

      {/* Pre-P8 punch list, Tier 2 (mobile): the edit form used to REPLACE its
          table row. Inside a table that now scrolls sideways on a phone, a
          480px form would sit half off-screen — and the admin who tapped Edit
          in the rightmost column is scrolled away from it. It renders up here
          instead (where "+ Add" already does), on every width for one
          consistent layout; the row keeps its place, is tinted, and says
          "Editing…", and the form itself takes focus (its "Editing X" caption
          is what a screen reader hears first — no keyboard pops up for a
          field the admin rarely changes). Closing it hands focus back to the
          row's Edit button. The form gets the row AS IT WAS WHEN EDIT WAS
          TAPPED, not a live lookup: that frozen row is the compare-and-set
          snapshot (lib/actions.ts, matchSnapshot), and a refresh mid-edit
          must not swap it under fields still seeded from the old values. */}
      {editingRow && (
        <ManagerEditForm
          key={editingRow.email}
          manager={editingRow}
          locations={locations}
          onCancel={() => closeEdit(true)}
          onSubmit={async (patch) => {
            const res = await updateManager(editingRow.email, patch, editingRow);
            setBanner(bannerFrom(res, `${editingRow.full_name} updated.`));
            if (res.ok) {
              closeEdit(true);
              onChanged();
            } else if (res.kind === 'stale') {
              closeEdit(false);
              onChanged();
            }
            return res;
          }}
        />
      )}

      <TableScroller labelledBy="managers-caption" minWidth="44rem">
        <table style={S.table}>
          <caption id="managers-caption" style={S.srOnly}>
            Managers and access
          </caption>
          <thead>
            <tr>
              <th style={S.th}>Name</th>
              <th style={S.th}>Email</th>
              <th style={S.th}>Role</th>
              <th style={S.th}>Location</th>
              <th style={S.th}>Status</th>
              <th style={S.thActions}>
                <span style={S.srOnly}>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {managers.map((m) => {
              const td = m.active ? S.td : S.tdInactive;
              const isEditing = editingEmail === m.email;
              return (
                <tr key={m.email} style={isEditing ? S.rowEditing : undefined}>
                  <td style={td}>{m.full_name}</td>
                  <td style={td}>{m.email}</td>
                  <td style={td}>
                    {m.role}
                    {m.role === 'admin' && <span style={S.adminBadge}>admin</span>}
                  </td>
                  <td style={td}>{locationName(locations, m.location_id)}</td>
                  <td style={td}>{m.active ? 'Active' : 'Inactive'}</td>
                  <td style={S.td}>
                    {isEditing ? (
                      <span style={S.editingTag}>Editing…</span>
                    ) : (
                      <button
                        ref={focus.register(m.email)}
                        style={S.linkBtn}
                        onClick={() => setEditingRow(m)}
                        disabled={removingEmail === m.email}
                      >
                        Edit
                      </button>
                    )}
                    {' · '}
                    {/* Not while that row's edit form is open: two write
                        paths on one row at once is how a hard-DELETE lands
                        under a form mid-edit (review finding). Cancel first. */}
                    <button
                      style={S.linkBtnDanger}
                      onClick={() => void remove(m)}
                      disabled={removingEmail === m.email || isEditing}
                    >
                      {removingEmail === m.email ? 'Removing…' : 'Remove'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroller>
    </div>
  );
}

function ManagerForm({
  locations,
  onCancel,
  onSubmit,
}: {
  locations: AdminLocationRow[];
  onCancel: () => void;
  onSubmit: (input: {
    email: string;
    fullName: string;
    role: 'manager' | 'admin';
    locationId: string | null;
  }) => Promise<ActionResult>;
}) {
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<'manager' | 'admin'>('manager');
  const [locationId, setLocationId] = useState<string>('');
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (!fullName.trim() || !email.trim()) return;
    setBusy(true);
    await onSubmit({
      email: email.trim().toLowerCase(),
      fullName: fullName.trim(),
      role,
      locationId: locationId || null,
    });
    setBusy(false);
  }

  return (
    <div style={S.form}>
      <Field label="Name">
        <input autoFocus value={fullName} onChange={(e) => setFullName(e.target.value)} style={S.input} />
      </Field>
      <Field label="Email">
        <input value={email} onChange={(e) => setEmail(e.target.value)} style={S.input} />
      </Field>
      <Field label="Role">
        <select value={role} onChange={(e) => setRole(e.target.value as 'manager' | 'admin')} style={S.input}>
          <option value="manager">Manager</option>
          <option value="admin">Admin</option>
        </select>
      </Field>
      {/* 0020 made this field do a second job, and an admin setting it had no
          way to know (adversarial-review finding): it is no longer only READ
          scope. A non-admin manager pinned to one location also gets that
          location's Roster page; "All locations" reads everything and edits no
          roster at all, which is a surprising way to withhold the feature from
          someone by being generous with their read access. */}
      <Field
        label="Location (blank = all locations)"
        hint="A manager assigned to one location can also add, edit and deactivate that location’s employees from their own Roster page. “All locations” sees every location but edits no roster. Admins manage every roster from here."
      >
        <select value={locationId} onChange={(e) => setLocationId(e.target.value)} style={S.input}>
          <option value="">All locations</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {locationOptionLabel(l)}
            </option>
          ))}
        </select>
      </Field>
      <div style={S.formActions}>
        <button onClick={() => void submit()} disabled={busy} style={S.saveBtn}>
          {busy ? 'Adding…' : 'Add manager'}
        </button>
        <button onClick={onCancel} style={S.cancelBtn}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function ManagerEditForm({
  manager,
  locations,
  onCancel,
  onSubmit,
}: {
  manager: ManagerRow;
  locations: AdminLocationRow[];
  onCancel: () => void;
  onSubmit: (patch: {
    full_name: string;
    role: 'manager' | 'admin';
    location_id: string | null;
    active: boolean;
  }) => Promise<ActionResult>;
}) {
  const [fullName, setFullName] = useState(manager.full_name);
  const [role, setRole] = useState<'manager' | 'admin'>(manager.role);
  const [locationId, setLocationId] = useState(manager.location_id ?? '');
  const [active, setActive] = useState(manager.active);
  const [busy, setBusy] = useState(false);
  const captionId = useId();
  const formRef = useRef<HTMLDivElement | null>(null);
  // Focus the form, not a field: the caption is announced, no keyboard pops
  // up (review finding). Mount only.
  useEffect(() => {
    formRef.current?.focus();
  }, []);

  async function submit() {
    setBusy(true);
    await onSubmit({
      full_name: fullName.trim(),
      role,
      location_id: locationId || null,
      active,
    });
    setBusy(false);
  }

  return (
    <div ref={formRef} style={S.form} role="group" aria-labelledby={captionId} tabIndex={-1}>
      {/* Pre-P8 punch list: this form has no Email field at all (email is the
          manager's primary key and login identity, locked once created), so
          without this caption there is NO visible confirmation of which
          manager is being edited once the table row is gone. */}
      <p id={captionId} style={S.editingCaption}>
        Editing {manager.full_name} · {manager.email}
      </p>
      <Field label="Name">
        <input value={fullName} onChange={(e) => setFullName(e.target.value)} style={S.input} />
      </Field>
      <Field label="Role">
        <select value={role} onChange={(e) => setRole(e.target.value as 'manager' | 'admin')} style={S.input}>
          <option value="manager">Manager</option>
          <option value="admin">Admin</option>
        </select>
      </Field>
      {/* 0020 made this field do a second job, and an admin setting it had no
          way to know (adversarial-review finding): it is no longer only READ
          scope. A non-admin manager pinned to one location also gets that
          location's Roster page; "All locations" reads everything and edits no
          roster at all, which is a surprising way to withhold the feature from
          someone by being generous with their read access. */}
      <Field
        label="Location (blank = all locations)"
        hint="A manager assigned to one location can also add, edit and deactivate that location’s employees from their own Roster page. “All locations” sees every location but edits no roster. Admins manage every roster from here."
      >
        <select value={locationId} onChange={(e) => setLocationId(e.target.value)} style={S.input}>
          <option value="">All locations</option>
          {locations.map((l) => (
            <option key={l.id} value={l.id}>
              {locationOptionLabel(l)}
            </option>
          ))}
        </select>
      </Field>
      <Field label="Status">
        <label style={S.checkboxLabel}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active — unchecking revokes access immediately, no re-login needed
        </label>
      </Field>
      <div style={S.formActions}>
        <button onClick={() => void submit()} disabled={busy} style={S.saveBtn}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel} style={S.cancelBtn}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Locations
// ---------------------------------------------------------------------------

function LocationsTab({
  locations,
  onChanged,
}: {
  locations: AdminLocationRow[];
  onChanged: () => void;
}) {
  const [banner, setBanner] = useState<Banner>(null);
  // The row as it was when Edit was tapped — frozen (see the note above the form).
  const [editingRow, setEditingRow] = useState<AdminLocationRow | null>(null);
  const editingId = editingRow?.id ?? null;
  const focus = useEditFocus<string>();
  const [addingName, setAddingName] = useState<string | null>(null);
  // Pre-P8 punch list, Tier 2: this was the one write button in the console
  // with no busy-guard against a double-submit — every other Save button
  // here already has one.
  const [busy, setBusy] = useState(false);
  // Pre-P8 punch list: same "Show inactive" pattern as the Roster tab —
  // deactivated locations (never deleted, since a real sent email can FK-
  // block the delete — M10) default to hidden, with a visible count as the
  // affordance so nothing reads as a silent gap.
  const [showInactive, setShowInactive] = useState(false);
  const inactiveCount = locations.filter((l) => !l.active).length;
  const visibleLocations = showInactive ? locations : locations.filter((l) => l.active);
  // Direction D pattern 4 (section heading) narrow override.
  const narrow = useIsNarrow();

  function closeEdit(handBackFocus: boolean) {
    const id = editingId;
    setEditingRow(null);
    if (handBackFocus && id !== null) focus.returnTo(id);
  }

  async function addLocation() {
    if (addingName === null || !addingName.trim()) return;
    setBusy(true);
    const res = await createLocation(addingName.trim());
    setBusy(false);
    setBanner(bannerFrom(res, `${addingName.trim()} added.`));
    if (res.ok) {
      setAddingName(null);
      onChanged();
    }
  }

  return (
    <div>
      <div style={S.tabHeader}>
        {/* Accessibility-review fix (redesign Pass 2): h3 -> h2, matching
            RosterTab's own comment above. */}
        <h2
          ref={focus.heading}
          tabIndex={-1}
          style={{ ...S.tabTitle, ...(narrow ? S.tabTitleNarrow : null) }}
        >
          Locations ({visibleLocations.length})
        </h2>
        <button style={S.addBtn} onClick={() => setAddingName(addingName === null ? '' : null)}>
          {addingName === null ? '+ Add location' : 'Cancel'}
        </button>
      </div>
      {inactiveCount > 0 && (
        <label style={S.showInactiveLabel}>
          <input
            type="checkbox"
            checked={showInactive}
            onChange={(e) => setShowInactive(e.target.checked)}
          />
          Show inactive ({inactiveCount})
        </label>
      )}
      <ResultBanner banner={banner} />

      {addingName !== null && (
        <div style={S.form}>
          <Field label="Name">
            <input autoFocus value={addingName} onChange={(e) => setAddingName(e.target.value)} style={S.input} />
          </Field>
          <div style={S.formActions}>
            <button onClick={() => void addLocation()} disabled={busy} style={S.saveBtn}>
              {busy ? 'Adding…' : 'Add'}
            </button>
            <button onClick={() => setAddingName(null)} disabled={busy} style={S.cancelBtn}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Pre-P8 punch list, Tier 2 (mobile): the edit form used to REPLACE its
          table row. Inside a table that now scrolls sideways on a phone, a
          480px form would sit half off-screen — and the admin who tapped Edit
          in the rightmost column is scrolled away from it. It renders up here
          instead (where "+ Add" already does), on every width for one
          consistent layout; the row keeps its place, is tinted, and says
          "Editing…", and the form itself takes focus (its "Editing X" caption
          is what a screen reader hears first — no keyboard pops up for a
          field the admin rarely changes). Closing it hands focus back to the
          row's Edit button. The form gets the row AS IT WAS WHEN EDIT WAS
          TAPPED, not a live lookup: that frozen row is the compare-and-set
          snapshot (lib/actions.ts, matchSnapshot), and a refresh mid-edit
          must not swap it under fields still seeded from the old values. */}
      {editingRow && (
        <LocationForm
          key={editingRow.id}
          location={editingRow}
          onCancel={() => closeEdit(true)}
          onSubmit={async (patch) => {
            const res = await updateLocation(editingRow.id, patch, editingRow);
            setBanner(bannerFrom(res, `${editingRow.name} updated.`));
            if (res.ok) {
              closeEdit(true);
              onChanged();
            } else if (res.kind === 'stale') {
              closeEdit(false);
              onChanged();
            }
            return res;
          }}
        />
      )}

      <TableScroller labelledBy="locations-caption" minWidth="30rem">
        <table style={S.table}>
          <caption id="locations-caption" style={S.srOnly}>
            Locations
          </caption>
          <thead>
            <tr>
              <th style={S.th}>Name</th>
              <th style={S.th}>Status</th>
              <th style={S.th}>Overrides</th>
              <th style={S.thActions}>
                <span style={S.srOnly}>Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {visibleLocations.map((l) => {
              const td = l.active ? S.td : S.tdInactive;
              return (
                <tr key={l.id} style={editingId === l.id ? S.rowEditing : undefined}>
                  <td style={td}>
                    {l.name}
                    {l.notes && <div style={S.tdSub}>{l.notes}</div>}
                  </td>
                  <td style={td}>{l.active ? 'Active' : 'Inactive'}</td>
                  <td style={td}>
                    {l.point_thresholds || l.notification_recipients?.length || l.pto_allotment != null ? (
                      <span style={S.tdSub}>customized</span>
                    ) : (
                      <span style={S.tdSub}>inherits company defaults</span>
                    )}
                  </td>
                  <td style={S.td}>
                    {editingId === l.id ? (
                      <span style={S.editingTag}>Editing…</span>
                    ) : (
                      <button ref={focus.register(l.id)} style={S.linkBtn} onClick={() => setEditingRow(l)}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </TableScroller>
    </div>
  );
}

function LocationForm({
  location,
  onCancel,
  onSubmit,
}: {
  location: AdminLocationRow;
  onCancel: () => void;
  onSubmit: (patch: {
    name: string;
    active: boolean;
    notes: string | null;
    point_thresholds: number[] | null;
    notification_recipients: string[] | null;
    pto_allotment: number | null;
  }) => Promise<ActionResult>;
}) {
  const [name, setName] = useState(location.name);
  const [active, setActive] = useState(location.active);
  const [notes, setNotes] = useState(location.notes ?? '');
  const [thresholds, setThresholds] = useState(location.point_thresholds?.join(',') ?? '');
  // Pre-P8 punch list, Tier 2: a list of addresses, edited as chips
  // (lib/EmailChipsInput.tsx), no longer one comma-joined string. An empty
  // list saves as NULL = "inherit the company default", exactly as a blank
  // text box did.
  const [recipients, setRecipients] = useState<string[]>(location.notification_recipients ?? []);
  const [allotment, setAllotment] = useState(location.pto_allotment?.toString() ?? '');
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);
  const captionId = useId();
  const recipientsId = useId();
  const recipientsHintId = useId();
  const recipientsRef = useRef<EmailChipsInputHandle>(null);
  const narrow = useIsNarrow();
  const formRef = useRef<HTMLDivElement | null>(null);
  // Focus the form, not a field (review finding). Mount only.
  useEffect(() => {
    formRef.current?.focus();
  }, []);

  async function submit() {
    setLocalError(null);
    let parsedThresholds: number[] | null;
    let parsedAllotment: number | null;
    try {
      parsedThresholds = parseNumberList(thresholds);
    } catch (e) {
      setLocalError(`Point thresholds: ${(e as Error).message}`);
      return;
    }
    try {
      parsedAllotment = parseAllotment(allotment, 'PTO allotment override', 'inherit');
    } catch (e) {
      setLocalError((e as Error).message);
      return;
    }
    // Text still sitting in the recipients box is committed NOW, and the
    // returned list — not this render's state — is what gets saved. Never
    // trust blur to have run first (every review lens flagged the case where
    // it had, failed, and Save then quietly dropped the address).
    const flushed = recipientsRef.current?.flush() ?? { ok: true as const, value: recipients };
    if (!flushed.ok) {
      // The chip input already shows the specific problem under itself; this
      // top-of-form line just says why Save did nothing (caught live: the
      // same sentence twice read as a stutter).
      setLocalError('Not saved — fix or clear the address still typed in Notification recipients first.');
      return;
    }
    setBusy(true);
    await onSubmit({
      name: name.trim(),
      active,
      notes: notes.trim() ? notes.trim() : null,
      point_thresholds: parsedThresholds,
      notification_recipients: flushed.value.length > 0 ? flushed.value : null,
      pto_allotment: parsedAllotment,
    });
    setBusy(false);
  }

  return (
    <div ref={formRef} style={S.form} role="group" aria-labelledby={captionId} tabIndex={-1}>
      {/* Pre-P8 punch list: same identity caption as the Roster/Managers edit
          forms — consistent across every editable form in the console. */}
      <p id={captionId} style={S.editingCaption}>
        Editing {location.name}
      </p>
      {localError && (
        <p role="alert" style={S.errorBanner}>
          {localError}
        </p>
      )}
      <Field label="Name">
        <input value={name} onChange={(e) => setName(e.target.value)} style={S.input} />
      </Field>
      <Field label="Status">
        <label style={S.checkboxLabel}>
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active — deactivating is blocked while anyone is still assigned here
        </label>
      </Field>
      <Field label="Notes">
        <input value={notes} onChange={(e) => setNotes(e.target.value)} style={S.input} />
      </Field>
      <Field label="Point thresholds override (e.g. 10,15,20 — blank = inherit company default)">
        <input value={thresholds} onChange={(e) => setThresholds(e.target.value)} style={S.input} />
      </Field>
      {/* Not <Field>: that wraps its child in a <label>, and a label around a
          list of Remove buttons would make every one of them "the" labelled
          control. A plain caption + htmlFor to the text box instead. */}
      <div style={S.field}>
        <label htmlFor={recipientsId} style={S.fieldLabel}>
          Notification recipients override
        </label>
        <span id={recipientsHintId} style={S.fieldHint}>
          Leave empty to inherit the company-wide list.
        </span>
        <EmailChipsInput
          ref={recipientsRef}
          id={recipientsId}
          value={recipients}
          onChange={setRecipients}
          disabled={busy}
          narrow={narrow}
          describedBy={recipientsHintId}
          placeholder="Add an address to override the company list"
        />
      </div>
      <Field label="PTO allotment override (blank = inherit)">
        <input
          inputMode="decimal"
          value={allotment}
          onChange={(e) => setAllotment(e.target.value)}
          style={S.input}
        />
      </Field>
      <div style={S.formActions}>
        <button onClick={() => void submit()} disabled={busy} style={S.saveBtn}>
          {busy ? 'Saving…' : 'Save'}
        </button>
        <button onClick={onCancel} style={S.cancelBtn}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Company Settings
// ---------------------------------------------------------------------------

function SettingsTab({
  settings,
  onChanged,
}: {
  settings: AdminCompanySettingsRow | null;
  onChanged: () => void;
}) {
  const [banner, setBanner] = useState<Banner>(null);
  const [localError, setLocalError] = useState<string | null>(null);
  const [thresholds, setThresholds] = useState(settings?.point_thresholds.join(',') ?? '');
  // Chips, not a comma-joined string — see LocationForm. Company-wide the
  // list may legitimately be empty (`not null default '{}'`), so [] is [].
  const [recipients, setRecipients] = useState<string[]>(settings?.notification_recipients ?? []);
  const [allotment, setAllotment] = useState(settings?.pto_allotment?.toString() ?? '');
  const [notifyEmployee, setNotifyEmployee] = useState(settings?.notify_employee ?? true);
  const [portalUrl, setPortalUrl] = useState(settings?.portal_url ?? '');
  const [busy, setBusy] = useState(false);
  const recipientsId = useId();
  const recipientsHintId = useId();
  const recipientsRef = useRef<EmailChipsInputHandle>(null);
  const narrow = useIsNarrow();

  // Re-seed the fields whenever the row changes underneath — the refresh
  // after a save (ours, or on a stale conflict someone else's). An effect
  // rather than a `key` remount of this tab: a remount also wiped the result
  // banner, which is state in this same component, so the stale message was
  // never seen (caught live during the overnight verification).
  const stamp = settings?.updated_at;
  useEffect(() => {
    if (!settings) return;
    setThresholds(settings.point_thresholds.join(','));
    setRecipients(settings.notification_recipients);
    setAllotment(settings.pto_allotment?.toString() ?? '');
    setNotifyEmployee(settings.notify_employee);
    setPortalUrl(settings.portal_url ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the version stamp on purpose
  }, [stamp]);

  if (!settings) {
    return <p style={S.errorBanner}>Company settings could not be loaded.</p>;
  }

  async function submit() {
    // Narrowed for the closure: the render above already returned early when
    // settings failed to load, so this only guards the type.
    if (!settings) return;
    setLocalError(null);
    let parsedThresholds: number[];
    let parsedAllotment: number | null;
    try {
      const parsed = parseNumberList(thresholds);
      if (!parsed || parsed.length !== 3) {
        setLocalError('Point thresholds need exactly three ascending numbers, e.g. 10,15,20.');
        return;
      }
      parsedThresholds = parsed;
    } catch (e) {
      setLocalError(`Point thresholds: ${(e as Error).message}`);
      return;
    }
    try {
      parsedAllotment = parseAllotment(allotment, 'Default PTO allotment', 'no limit');
    } catch (e) {
      setLocalError((e as Error).message);
      return;
    }
    // See LocationForm.submit: commit pending recipient text now, save the returned list.
    const flushed = recipientsRef.current?.flush() ?? { ok: true as const, value: recipients };
    if (!flushed.ok) {
      // The chip input already shows the specific problem under itself; this
      // top-of-form line just says why Save did nothing (caught live: the
      // same sentence twice read as a stutter).
      setLocalError('Not saved — fix or clear the address still typed in Notification recipients first.');
      return;
    }
    setBusy(true);
    const res = await updateCompanySettings(
      {
        point_thresholds: parsedThresholds,
        notification_recipients: flushed.value,
        pto_allotment: parsedAllotment,
        notify_employee: notifyEmployee,
        portal_url: portalUrl.trim() ? portalUrl.trim() : null,
      },
      // The row's trigger-maintained stamp as loaded — the compare-and-set
      // version (lib/actions.ts). If another admin saved settings since this
      // page loaded, this refuses as 'stale' instead of overwriting theirs.
      { updated_at: settings.updated_at },
    );
    setBusy(false);
    setBanner(bannerFrom(res, 'Company settings saved.'));
    if (res.ok || res.kind === 'stale') onChanged();
  }

  return (
    <div>
      {/* Accessibility-review fix (redesign Pass 2): h3 -> h2, matching
          the other tabs' own comment. */}
      <h2 style={{ ...S.tabTitle, ...(narrow ? S.tabTitleNarrow : null) }}>Company Settings</h2>
      <p style={S.tdSub}>
        These are the company-wide defaults. Any location can override point
        thresholds, notification recipients, or PTO allotment individually in
        the Locations tab — a blank override there inherits what is set here.
      </p>
      <ResultBanner banner={banner} />
      {localError && (
        <p role="alert" style={S.errorBanner}>
          {localError}
        </p>
      )}

      <div style={S.form}>
        <Field label="Point thresholds (Verbal, Written, Final — ascending)">
          <input value={thresholds} onChange={(e) => setThresholds(e.target.value)} style={S.input} />
        </Field>
        <div style={S.field}>
          <label htmlFor={recipientsId} style={S.fieldLabel}>
            Notification recipients
          </label>
          <span id={recipientsHintId} style={S.fieldHint}>
            Who is emailed when a request comes in or is decided. Locations can override this list.
          </span>
          <EmailChipsInput
            ref={recipientsRef}
            id={recipientsId}
            value={recipients}
            onChange={setRecipients}
            disabled={busy}
            narrow={narrow}
            describedBy={recipientsHintId}
          />
        </div>
        <Field label="Default PTO allotment (blank = no limit)">
          <input
            inputMode="decimal"
            value={allotment}
            onChange={(e) => setAllotment(e.target.value)}
            style={S.input}
          />
        </Field>
        <Field label="Portal URL (used in email links)">
          <input value={portalUrl} onChange={(e) => setPortalUrl(e.target.value)} style={S.input} />
        </Field>
        <Field label="Notifications">
          <label style={S.checkboxLabel}>
            <input
              type="checkbox"
              checked={notifyEmployee}
              onChange={(e) => setNotifyEmployee(e.target.checked)}
            />
            Email employees on decisions and threshold warnings
          </label>
        </Field>
        <div style={S.formActions}>
          <button onClick={() => void submit()} disabled={busy} style={S.saveBtn}>
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Health Checks (P7) — read-only, report-never-auto-fix (the v21 auto-
// backfill incident is exactly why: this page names a problem, it never
// touches a row). One line per offending record, grouped by severity so the
// two 🔴-shaped checks (a wiped exclusion reason, a stuck outbox email) sort
// to the top rather than getting lost among the merely informational ones.
// ---------------------------------------------------------------------------

const SEVERITY_LABEL: Record<HealthCheckSeverity, string> = {
  critical: 'Needs attention',
  warning: 'Worth a look',
  info: 'For your information',
};

const SEVERITY_ORDER: HealthCheckSeverity[] = ['critical', 'warning', 'info'];

function HealthChecksTab({
  findings,
  onRefresh,
}: {
  findings: HealthCheckFinding[];
  onRefresh: () => void;
}) {
  const [refreshing, setRefreshing] = useState(false);
  // Direction D pattern 4 (section heading) narrow override.
  const narrow = useIsNarrow();

  async function refresh() {
    setRefreshing(true);
    onRefresh();
    // onRefresh() re-fires loadAdminData(), which is async but fire-and-
    // forget from here — this button's own "Refreshing…" state is cosmetic
    // (data itself arrives via the parent's normal re-render), so a short,
    // fixed pause is enough to make the click feel acknowledged without
    // wiring a second loading flag through props for a read-only page.
    setTimeout(() => setRefreshing(false), 400);
  }

  return (
    <div>
      <div style={S.tabHeader}>
        {/* Accessibility-review fix (redesign Pass 2): h3 -> h2, matching
            the other tabs' own comment; the group headings below shift
            h4 -> h3 to stay one level under this. */}
        <h2 style={{ ...S.tabTitle, ...(narrow ? S.tabTitleNarrow : null) }}>
          Health Checks ({findings.length})
        </h2>
        <button style={S.addBtn} onClick={() => void refresh()} disabled={refreshing}>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>
      <p style={S.tdSub}>
        Read-only — nothing here writes to a record. Void or correct anything
        it names from Manage Records, Roster, or Managers &amp; Access.
      </p>

      {findings.length === 0 ? (
        <p style={S.tdSub}>No issues found.</p>
      ) : (
        SEVERITY_ORDER.map((severity) => {
          const group = findings.filter((f) => f.severity === severity);
          if (group.length === 0) return null;
          return (
            <div key={severity} style={S.healthGroup}>
              <h3 style={S.healthGroupTitle}>
                {SEVERITY_LABEL[severity]} ({group.length})
              </h3>
              <ul style={S.healthList}>
                {group.map((f, i) => (
                  <li
                    key={`${f.check_name}-${i}`}
                    style={{ ...healthItemBase, borderLeftColor: healthSeverityColor(severity) }}
                  >
                    {f.detail}
                  </li>
                ))}
              </ul>
            </div>
          );
        })
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small shared pieces + styles
// ---------------------------------------------------------------------------

/**
 * A labelled field. `hint` is a sentence under the control.
 *
 * `plain` renders a read-only VALUE instead of a control — 0020's Location and
 * PTO allotment, for a manager who may see them but not change them.
 *
 * 🔴 A plain field must NOT reuse the <label> above it. A <label> with no
 * labelable descendant names nothing, and the value markup inside one is
 * invalid HTML besides, so a screen reader announces the label text and the
 * value as two unrelated runs rather than as a pair — which is precisely what
 * a read-only field needs to convey. <dl>/<dt>/<dd> is the markup that says
 * "this term has this value" natively. Adversarial-review finding, 2026-09-04.
 */
function Field({
  label,
  children,
  hint,
  plain = false,
}: {
  label: string;
  children: React.ReactNode;
  hint?: React.ReactNode;
  plain?: boolean;
}) {
  if (plain) {
    return (
      <dl style={S.plainField}>
        <dt style={S.fieldLabel}>{label}</dt>
        <dd style={S.readOnlyValue}>
          {children}
          {hint && <span style={S.readOnlyNote}>{hint}</span>}
        </dd>
      </dl>
    );
  }
  return (
    <label style={S.field}>
      <span style={S.fieldLabel}>{label}</span>
      {children}
      {hint && <span style={S.fieldHint}>{hint}</span>}
    </label>
  );
}

// Direction D re-skin (2026-09-04): every value below is a straight token
// swap from src/lib/theme.ts, following the exact patterns already shipped
// in Dashboard.tsx / AppShell.tsx / LogAbsence.tsx — no new combinations
// invented. AdminConsole/ManagerRoster's chrome (sidebar/tab bar) was
// already migrated in an earlier pass; this covers the page's OWN content
// (title, tab nav, cards, tables, forms, chips, banners). Component
// structure, props, and heading tags are unchanged — style values only.
const S = {
  wrap: { maxWidth: 960, margin: '0 auto' },
  // Direction D pattern 1 (page title) — same 24px/20px pairing as
  // LogAbsence.tsx's own h1 (a secondary, single-purpose page, not the
  // busiest screen in the app).
  title: {
    // Cross-file consistency review: margin moved to 0 (was '0 0 6px') to
    // match LogAbsence.tsx/ManageRecords.tsx's own title+subtitle spacing —
    // the 4px gap now lives on `sub`'s marginTop below, same place they put it.
    margin: 0,
    fontSize: '24px',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    fontFamily: font.display,
    color: color.ink,
  },
  titleNarrow: { fontSize: '20px' },
  // Direction D pattern 2 (page subtitle).
  sub: { color: color.textMuted, fontSize: '14px', lineHeight: 1.5, marginTop: '4px', marginBottom: '20px' },
  tabs: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' },
  // The Roster / Managers & Access / Locations / Company Settings / Health
  // Checks row — a distinct control from AppShell's own sidebar nav, styled
  // to read clearly against the new card/page background: a neutral pill
  // when inactive, a filled brand-blue pill when active (same active
  // treatment as AppShell's own S.navItemActive).
  tabBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '2.75rem',
    padding: '0 18px',
    borderRadius: radius.pill,
    border: 0,
    background: color.pillNeutralBg,
    color: color.textSubtle,
    fontSize: '13.5px',
    fontWeight: 600,
    fontFamily: font.body,
    cursor: 'pointer',
  },
  tabBtnActive: { background: color.brandBlue, color: 'white', fontFamily: font.display, fontWeight: 700 },
  // Direction D pattern 3 (card container).
  card: {
    border: `1px solid ${color.cardBorder}`,
    borderRadius: radius.cardDesktop,
    padding: '26px 28px 22px',
    background: color.cardBg,
    boxShadow: shadow.card,
  },
  cardNarrow: { borderRadius: radius.cardMobile, padding: '16px', boxShadow: 'none' },
  tabHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '16px',
    flexWrap: 'wrap',
    gap: '8px',
  },
  // Direction D pattern 4 (section heading) — same shape as Dashboard.tsx's
  // own SectionCard sectionTitle/sectionTitleNarrow pair.
  tabTitle: {
    marginTop: 0,
    marginRight: 0,
    marginBottom: '18px',
    marginLeft: 0,
    fontSize: '17px',
    fontWeight: 700,
    fontFamily: font.display,
    color: color.ink,
  },
  tabTitleNarrow: { fontSize: '15px', marginBottom: '14px' },
  // Direction D pattern 7 (small pill utility button) — "+ Add employee" /
  // "+ Add manager" / "+ Add location" / Health Checks' "Refresh" are all
  // exactly the lower-weight utility control this pattern names.
  addBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '2.75rem',
    padding: '7px 16px',
    borderRadius: radius.pill,
    border: 0,
    background: color.pillNeutralBg,
    color: color.textSubtle,
    fontSize: '12.5px',
    fontWeight: 600,
    fontFamily: font.body,
    cursor: 'pointer',
  },
  // Direction D pattern 10 (table).
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13.5px', fontFamily: font.body, color: color.ink },
  th: {
    textAlign: 'left',
    // Cross-file consistency review: matches Dashboard.tsx's own th padding
    // exactly — this file's td already used the matching right-gutter
    // convention, th had been left on the old literal.
    padding: '0.5rem 0.75rem 0.5rem 0',
    fontSize: '10.5px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: color.textFaint,
    borderBottom: `1px solid ${color.cardBorder}`,
  },
  td: { padding: '0.65rem 0.75rem 0.65rem 0', borderBottom: `1px solid ${color.pageBg}`, verticalAlign: 'top' },
  tdSub: { color: color.textMuted, fontSize: '0.78rem' },
  // Applied per DATA cell (not the <tr>) so the actions cell keeps full
  // contrast — see the Roster rows.
  tdInactive: {
    padding: '0.65rem 0.75rem 0.65rem 0',
    borderBottom: `1px solid ${color.pageBg}`,
    verticalAlign: 'top',
    opacity: 0.55,
  },
  // The row whose edit form is open above the table: readable from any
  // horizontal scroll position, not only from the actions column.
  rowEditing: { background: color.chipBrandBg },
  // The empty-looking actions header carries a visually-hidden "Actions"
  // label; that label is position:absolute, so the cell must be its
  // containing block — otherwise it lands at the table's far right edge and
  // widens the PAGE by a few pixels (caught live).
  thActions: {
    textAlign: 'left',
    padding: '0.5rem 0.75rem 0.5rem 0',
    fontSize: '10.5px',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: color.textFaint,
    borderBottom: `1px solid ${color.cardBorder}`,
    position: 'relative',
  },
  // Visually hidden, still read by screen readers (the standard clip trick).
  srOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  editingCaption: { fontWeight: 700, fontSize: '0.95rem', margin: '0 0 0.9rem', color: color.ink },
  // Direction D pattern 7 (small pill toggle) — "Show inactive (N)".
  showInactiveLabel: {
    display: 'inline-flex',
    alignItems: 'center',
    minHeight: '2.75rem',
    gap: '6px',
    fontSize: '12.5px',
    fontWeight: 600,
    fontFamily: font.body,
    color: color.textSubtle,
    background: color.pillNeutralBg,
    borderRadius: radius.pill,
    padding: '0 16px',
    marginBottom: '12px',
    cursor: 'pointer',
  },
  editingTag: { fontSize: '0.85rem', fontWeight: 600, color: color.brandBlue },
  // Direction D pattern 12 (inline text link).
  linkBtn: {
    background: 'none',
    border: 'none',
    color: color.brandBlue,
    cursor: 'pointer',
    padding: 0,
    fontSize: '0.85rem',
    fontWeight: 600,
    fontFamily: font.body,
  },
  // Same shape as linkBtn, in the established error-red pair.
  linkBtnDanger: {
    background: 'none',
    border: 'none',
    color: '#b00020',
    cursor: 'pointer',
    padding: 0,
    fontSize: '0.85rem',
    fontWeight: 600,
    fontFamily: font.body,
  },
  // Direction D pattern 11 (chip/badge), brand variant.
  adminBadge: {
    marginLeft: '0.4rem',
    display: 'inline-block',
    padding: '0.05rem 0.45rem',
    borderRadius: radius.pill,
    background: color.chipBrandBg,
    color: color.brandBlue,
    fontSize: '0.68rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.7rem',
    padding: '16px',
    background: color.pageBg,
    border: `1px solid ${color.cardBorder}`,
    borderRadius: '14px',
    marginBottom: '1rem',
    maxWidth: 480,
  },
  field: { display: 'flex', flexDirection: 'column', gap: '0.25rem' },
  fieldLabel: { fontSize: '0.78rem', fontWeight: 600, color: color.textSubtle },
  fieldHint: { fontSize: '0.78rem', color: color.textMuted, lineHeight: 1.4 },
  // Direction D pattern 13 (form inputs). 1rem = 16px: stops iOS Safari's
  // focus auto-zoom (RequestTimeOff.tsx's S.input has the story) — do not
  // shrink this on any breakpoint.
  input: {
    padding: '0.5rem 0.7rem',
    borderRadius: '0.6rem',
    border: `1px solid ${color.cardBorder}`,
    fontSize: '1rem',
    fontFamily: font.body,
    background: color.cardBg,
    color: color.ink,
  },
  checkboxLabel: { display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: color.ink },
  // 0020: a field a manager may see but not change. Deliberately NOT a
  // disabled input — a greyed-out control invites tapping and reads as
  // broken, and its 16px sizing would claim space the caption does not need.
  plainField: { display: 'flex', flexDirection: 'column', gap: '0.25rem', margin: 0 },
  // Direction D pattern 12 (inline text link).
  inlineLink: { color: color.brandBlue, textDecoration: 'underline' },
  readOnlyValue: {
    // <dd> carries a default margin-inline-start of 40px — reset it, or every
    // read-only value sits indented away from its own label.
    margin: 0,
    marginInlineStart: 0,
    fontSize: '1rem',
    color: color.ink,
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
  },
  readOnlyNote: { fontSize: '0.78rem', color: color.textMuted },
  formActions: { display: 'flex', gap: '0.6rem', marginTop: '0.3rem' },
  // Direction D pattern 5 (primary action button).
  saveBtn: {
    padding: '0.55rem 1.1rem',
    minHeight: '2.75rem',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: font.display,
    color: 'white',
    background: color.brandBlue,
    border: 0,
    borderRadius: radius.pill,
    cursor: 'pointer',
  },
  // Direction D pattern 6 (secondary/neutral button).
  cancelBtn: {
    padding: '0.55rem 1.1rem',
    minHeight: '2.75rem',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: font.display,
    color: color.textSubtle,
    background: 'white',
    border: `1.5px solid ${color.cardBorder}`,
    borderRadius: radius.pill,
    cursor: 'pointer',
  },
  bannerText: { margin: 0 },
  // No named Direction D pattern covers a success banner (8/9 are
  // warning/error only) — same choice LogAbsence.tsx already made: borrow the
  // shape and reuse the theme's teal token rather than inventing a new hex.
  successBanner: {
    margin: '0.85rem 0',
    padding: '0.45rem 0.7rem',
    borderRadius: '0.6rem',
    background: color.chipTealBg,
    color: color.teal,
    fontSize: '0.85rem',
    lineHeight: 1.45,
  },
  // Direction D pattern 9 (error banner) — the established '#fdecef'/
  // '#b00020' pair, verbatim, same border as LogAbsence.tsx's own S.error.
  errorBanner: {
    margin: '0.85rem 0',
    padding: '0.45rem 0.7rem',
    borderRadius: '0.6rem',
    background: '#fdecef',
    color: '#b00020',
    // Cross-file consistency review: matches ManageRecords.tsx's own
    // pre-existing border hex for the same error-banner role, rather than
    // this file's own separately-invented #f3c6cf.
    border: '1px solid #e5b3bb',
    fontSize: '0.85rem',
    lineHeight: 1.45,
  },
  // Direction D pattern 11 (chip/badge), neutral variant.
  tabBadge: {
    marginLeft: '0.35rem',
    display: 'inline-block',
    padding: '0.15rem 0.55rem',
    borderRadius: radius.pill,
    background: color.pillNeutralBg,
    color: color.textSubtle,
    fontSize: '0.7rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  // Direction D pattern 11 (chip/badge), red/over variant.
  tabBadgeCritical: {
    marginLeft: '0.35rem',
    display: 'inline-block',
    padding: '0.15rem 0.55rem',
    borderRadius: radius.pill,
    background: '#fdecef',
    color: '#b00020',
    fontSize: '0.7rem',
    fontWeight: 700,
    whiteSpace: 'nowrap',
  },
  healthGroup: { marginBottom: '1.1rem' },
  healthGroupTitle: {
    fontSize: '13px',
    fontWeight: 700,
    fontFamily: font.display,
    color: color.ink,
    margin: '0 0 0.5rem',
  },
  healthList: { listStyle: 'none', margin: 0, padding: 0, fontSize: '0.87rem' },
} satisfies Record<string, React.CSSProperties>;

/**
 * Severity-tinted left border on each finding — critical reads loud (red),
 * warning amber, info quiet grey. Not part of the static S object above
 * (which `satisfies Record<string, React.CSSProperties>` and so cannot hold
 * a function), since the color depends on which group is rendering it.
 * Direction D re-skin: same three tones as the rest of this file — the
 * established error red, theme.ts's amberDark, and textFaint for info.
 */
function healthSeverityColor(severity: HealthCheckSeverity): string {
  switch (severity) {
    case 'critical':
      return '#b00020';
    case 'warning':
      return color.amberDark;
    case 'info':
      return color.textFaint;
  }
}

const healthItemBase: React.CSSProperties = {
  padding: '0.5rem 0.7rem',
  marginBottom: '0.35rem',
  borderRadius: '8px',
  background: color.pageBg,
  borderLeft: '3px solid transparent',
};
